import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { useUserMetadata } from "@/hooks/useUserMetadata";
import { Headset, X } from "lucide-react";
import { useEffect, useState } from "react";
import { UserRoleClaim } from "supertokens-auth-react/recipe/userroles";
import Session from "supertokens-auth-react/recipe/session";
const useCrisp = () => {
  const claimValue = Session.useClaimValue(UserRoleClaim);
  const userRoles = claimValue.value;

  useEffect(() => {
    window.$crisp = window.$crisp || [];
    window.CRISP_WEBSITE_ID = "d56d9279-4778-4333-b72b-05849205f3d9";

    const script = document.createElement("script");
    script.src = "https://client.crisp.chat/l.js";
    script.async = true;
    document.head.appendChild(script);
    const segments = [
      ...(Array.isArray(userRoles) ? userRoles : []),
      "customer",
    ];
    script.onload = () => {
      // Hide Crisp's default chat button
      window.$crisp.push(["do", "chat:hide"]);
      window.$crisp.push(["set", "session:segments", [segments]]);
    };

    return () => {
      document.head.removeChild(script);
    };
  }, []);
};

const CrispChat = ({
  text = "Chat with live support",
  isIconOnly = false,
  className,
  ...props
}) => {
  useCrisp(); // Replace with your Crisp Website ID
  const { data: user, isLoading } = useUserMetadata();

  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    // hide chat when it gets closed
    window.$crisp.push([
      "on",
      "chat:closed",
      () => {
        window.$crisp.push(["do", "chat:hide"]);
        setChatOpen(false);
      },
    ]);
    // view chat when a message is received
    window.$crisp.push([
      "on",
      "message:received",
      () => {
        window.$crisp.push(["do", "chat:show"]);
        setChatOpen(true);
      },
    ]);
    if (user) {
      if (user?.email) {
        window.$crisp.push(["set", "user:email", user.email]);
      }
      if (user?.name) {
        window.$crisp.push(["set", "user:nickname", user.name]);
      }
      if (user?.picture) {
        window.$crisp.push(["set", "user:avatar", user.picture]);
      }
    }
  }, [user, chatOpen, isLoading]);
  if (isLoading) return null;

  /**
   * Toggle the visibility of the Crisp chat widget.
   * @returns {undefined}
   */
  const toggleChat = () => {
    if (window.$crisp.is("chat:opened")) {
      window.$crisp.push(["do", "chat:hide"]);
      window.$crisp.push(["do", "chat:close"]);
      setChatOpen(false);
    } else {
      window.$crisp.push(["do", "chat:show"]);
      window.$crisp.push(["do", "chat:open"]);
      setChatOpen(true);
    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={toggleChat}
            className={className}
            disabled={isLoading}
            {...props}
          >
            {chatOpen ? <X /> : <Headset />}

            {!isIconOnly && (chatOpen ? "Close Chat" : text)}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p> {chatOpen ? "Close Chat" : "Live support"}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export { CrispChat, useCrisp };
